<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-emerald-50">
    <!-- Background decoration -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div class="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute top-40 left-40 w-80 h-80 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
    </div>

    <!-- Login form -->
    <div class="relative z-10 w-full max-w-md px-6">
      <!-- Logo/Brand section -->
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl mb-4 shadow-lg">
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-gray-800 mb-2">{{ $t('auth.login.welcome') }}</h1>
        <p class="text-gray-700">{{ $t('auth.login.subtitle') }}</p>
      </div>

      <!-- Login card -->
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <ClientOnly>
          <UForm 
            :state="form"
            @submit="handleLogin"
          >
            <UFormGroup
              :label="$t('auth.login.username')"
              required
              class="mb-6 !text-gray-800"
            >
              <input
                v-model="form.username"
                name="username"
                type="text"
                autocomplete="username"
                :disabled="authStore.isLoading"
                :placeholder="$t('auth.login.username_placeholder')"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500 focus:ring-opacity-20 text-gray-900 placeholder-gray-500 transition-colors duration-200"
              />
            </UFormGroup>

            <UFormGroup
              :label="$t('auth.login.password')"
              required
              class="mb-6 !text-gray-800"
            >
              <input
                v-model="form.password"
                name="password"
                type="password"
                autocomplete="current-password"
                :disabled="authStore.isLoading"
                :placeholder="$t('auth.login.password_placeholder')"
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500 focus:ring-opacity-20 text-gray-900 placeholder-gray-500 transition-colors duration-200"
              />
            </UFormGroup>

            <div class="flex items-center justify-between mb-6">
              <UCheckbox
                v-model="form.rememberMe"
                :label="$t('auth.login.remember_me')"
                name="remember-me"
                :disabled="authStore.isLoading"
                class="text-sm !text-gray-800"
              />
              <UButton
                variant="link"
                color="green"
                to="#"
                size="sm"
                class="text-sm font-medium"
              >
                {{ $t('auth.login.forgot_password') }}
              </UButton>
            </div>

            <UAlert
              v-if="authStore.error"
              type="danger"
              :title="$t('common.error')"
              :description="authStore.error"
              class="mb-6"
            />

            <UButton
              type="submit"
              :loading="authStore.isLoading"
              :disabled="authStore.isLoading"
              block
              class="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {{ authStore.isLoading ? $t('auth.login.signing_in') : $t('auth.login.sign_in') }}
            </UButton>

            <div class="mt-6 text-center">
              <span class="text-gray-700 text-sm">{{ $t('auth.login.no_account') }}</span>
              <UButton
                variant="link"
                color="green"
                to="/auth/register"
                size="sm"
                class="text-sm font-medium ml-1"
              >
                {{ $t('auth.login.sign_up_link') }}
              </UButton>
            </div>
          </UForm>
        </ClientOnly>
      </div>

      <!-- Footer -->
      <div class="text-center mt-8">
        <p class="text-gray-500 text-sm">
          {{ $t('footer.copyright') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '~/stores/auth'
import type { LoginCredentials } from '~/types/auth'

const router = useRouter()
const localePath = useLocalePath()
const authStore = useAuthStore()
const toast = {
  add: (msg: any) => console.log('Toast:', msg)
}
const { t } = useI18n()

const form = ref<LoginCredentials>({
  username: '',
  password: '',
  rememberMe: false
})

const handleLogin = async () => {
  if (!form.value.username || !form.value.password) {
    toast.add({
      title: t('common.error'),
      description: t('validation.required_fields'),
      color: 'red'
    })
    return
  }

  const result = await authStore.login(form.value)

  if (!result.success) {
    toast.add({
      title: t('common.error'),
      description: authStore.error || t('auth.login.error.login_failed'),
      color: 'red'
    })
    return
  }

  if (result.otpRequired) {
    toast.add({
      title: t('auth.otp.title'),
      description: t('auth.otp.subtitle'),
      color: 'blue'
    })
    router.push(localePath('/auth/verify-otp'))
    return
  }

  toast.add({
    title: t('common.success'),
    description: t('auth.login.success'),
    color: 'green'
  })
  router.push(localePath(authStore.dashboardPath))
}
</script>

<style scoped>
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Force light styling for form inputs */
:deep(.form-input) {
  background-color: white !important;
  color: #374151 !important;
  border-color: #d1d5db !important;
}

:deep(.form-input:focus) {
  border-color: #10b981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

:deep(.form-input::placeholder) {
  color: #9ca3af !important;
}

/* Force light styling for form groups */
:deep(.form-group) {
  color: #374151 !important;
}

/* Force darker text for form labels */
:deep(.form-group label) {
  color: #1f2937 !important;
  font-weight: 500 !important;
}

/* Force darker text for checkbox labels */
:deep(.form-checkbox + label) {
  color: #1f2937 !important;
}

/* Force darker text for any label elements */
:deep(label) {
  color: #1f2937 !important;
}

/* Target specific UI component classes */
:deep(.ui-form-group-label) {
  color: #1f2937 !important;
}

:deep(.ui-checkbox-label) {
  color: #1f2937 !important;
}

:deep(.ui-form-group) {
  color: #1f2937 !important;
}

/* Target any text that might be light */
:deep(.text-gray-400),
:deep(.text-gray-500),
:deep(.text-gray-600) {
  color: #1f2937 !important;
}

/* Force light styling for checkboxes */
:deep(.form-checkbox) {
  background-color: white !important;
  border-color: #d1d5db !important;
}

:deep(.form-checkbox:checked) {
  background-color: #10b981 !important;
  border-color: #10b981 !important;
}
</style> 